import React, { useState, useEffect, useRef } from 'react';
import Nav from '../components/nav';
import SEO from '../components/SEO';
import { useNavigate } from 'react-router-dom';
import { baseUrl } from '../config';

const GrainOverlay = () => {
  const canvasRef = useRef(null);
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    c.width = 256; c.height = 256;
    const id = ctx.createImageData(256, 256);
    for (let i = 0; i < id.data.length; i += 4) {
      const v = Math.random() * 30;
      id.data[i] = id.data[i+1] = id.data[i+2] = v;
      id.data[i+3] = 28;
    }
    ctx.putImageData(id, 0, 0);
  }, []);
  return (
    <canvas ref={canvasRef} style={{
      position: 'fixed', inset: 0, width: '100%', height: '100%',
      pointerEvents: 'none', zIndex: 1, opacity: 0.4
    }} />
  );
};

const Counter = ({ to, suffix = '' }) => {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      let start = 0;
      const step = () => {
        start += Math.ceil((to - start) / 8) || 1;
        setVal(Math.min(start, to));
        if (start < to) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
      obs.disconnect();
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [to]);
  return <span ref={ref}>{val.toLocaleString('en-IN')}{suffix}</span>;
};

const TOOLS = [
  {
    id: 'Script Analysis',
    name: 'Script Analysis',
    icon: '⌨',
    tagline: 'Breakdown in minutes, not days',
    detail: 'AI reads your script and generates character maps, scene complexity scores, department callouts and production notes — all instantly.',
    accent: '#7c3aed',
  },
  {
    id: 'Budgeting',
    name: 'Budgeting',
    icon: '₹',
    tagline: 'Real Indian market costs, not guesses',
    detail: 'Intelligent budget forecasting built on actual Indian production data. Department-wise breakdowns, contingency planning, revision tracking.',
    accent: '#0369a1',
  },
  {
    id: 'Casting Assistant',
    name: 'Casting Assistant',
    icon: '◉',
    tagline: 'Find the right talent, faster',
    detail: 'AI-driven character-to-talent matching with audition scheduling, availability tracking and team collaboration built in.',
    accent: '#b45309',
  },
  {
    id: 'Location Scouting',
    name: 'Location Scouting',
    icon: '◎',
    tagline: 'Scout India without leaving your desk',
    detail: 'Virtual location discovery across India. Logistics planning, permit checklists, cost estimates and shot-list matching.',
    accent: '#047857',
  },
];

export default function EarlyAccessPage() {
  const navigate = useNavigate();
  const [user, setUser]               = useState(null);
  const [selectedTool, setSelectedTool] = useState(null);
  const [submitting, setSubmitting]   = useState(false);
  const [done, setDone]               = useState(false);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [visible, setVisible]         = useState(false);
  const [hoveredTool, setHoveredTool] = useState(null);
  const [error, setError]             = useState('');

  // ── Auth check ── mirrors login page pattern exactly ────────────────────────
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) { navigate('/login'); return; }

    (async () => {
      try {
        // 1. Get user ID + basic info
        const r = await fetch(`${baseUrl}/getUserIDByToken`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!r.ok) { navigate('/login'); return; }
        const userData = await r.json();
        setUser(userData);

        // 2. Check if already registered for early access
        const check = await fetch(`${baseUrl}/earlyAccess/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (check.ok) {
          const existing = await check.json();
          setSelectedTool(existing.tool_interest);
          setAlreadyRegistered(true);
          setDone(true);
        }
      } catch {
        navigate('/login');
      }
    })();

    setTimeout(() => setVisible(true), 80);
  }, [navigate]);

  // ── Dashboard routing — same pattern as login page ───────────────────────────
  const goToDashboard = () => {
    const dashboardRoute = localStorage.getItem('user_dashboard') || '/dashboard';
    navigate(dashboardRoute);
  };

  // ── Reserve early access ─────────────────────────────────────────────────────
  const handleReserve = async () => {
    if (!selectedTool) return;
    setSubmitting(true);
    setError('');
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${baseUrl}/earlyAccess`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          tool_interest: selectedTool,
          plan_interest: null,
          notes: null
        })
      });

      if (res.ok) {
        setDone(true);
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || 'Something went wrong. Please try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const tool = TOOLS.find(t => t.id === selectedTool);
  // Display name: username > first_name > "Member #id"
  const displayName = user?.username || user?.first_name || (user?.user_id ? `Member #${user.user_id}` : '');

  return (
    <>
      <SEO
        title="Get Early Access"
        description="Be among the first to unlock ProducerBuddy's full AI-powered film production suite. Sign up for early access and get exclusive benefits for filmmakers worldwide."
        url="https://producerbuddy.pilomation.com/early-access"
      />
      <GrainOverlay />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400;1,600&family=DM+Mono:wght@400;500&family=DM+Sans:wght@300;400;500&display=swap');
        @keyframes fadeSlideUp { from{opacity:0;transform:translateY(24px);}to{opacity:1;transform:translateY(0);} }
        @keyframes fadeIn { from{opacity:0;}to{opacity:1;} }
        @keyframes pulse-ring { 0%,100%{box-shadow:0 0 0 0 rgba(124,58,237,0.35);}50%{box-shadow:0 0 0 12px rgba(124,58,237,0);} }
        @keyframes float { 0%,100%{transform:translateY(0);}50%{transform:translateY(-6px);} }
        @keyframes ticker { 0%{transform:translateX(0);}100%{transform:translateX(-50%);} }
        .anim-0{animation:fadeSlideUp 0.65s cubic-bezier(.22,1,.36,1) both;}
        .anim-1{animation:fadeSlideUp 0.65s 0.1s cubic-bezier(.22,1,.36,1) both;}
        .anim-2{animation:fadeSlideUp 0.65s 0.2s cubic-bezier(.22,1,.36,1) both;}
        .anim-3{animation:fadeSlideUp 0.65s 0.3s cubic-bezier(.22,1,.36,1) both;}
        .anim-4{animation:fadeSlideUp 0.65s 0.4s cubic-bezier(.22,1,.36,1) both;}
        .tool-card{transition:transform 0.25s cubic-bezier(.22,1,.36,1),box-shadow 0.25s,border-color 0.2s;cursor:pointer;}
        .tool-card:hover{transform:translateY(-4px);}
        .reserve-btn{transition:all 0.2s cubic-bezier(.22,1,.36,1);}
        .reserve-btn:hover:not(:disabled){transform:scale(1.03);}
        .reserve-btn:active:not(:disabled){transform:scale(0.98);}
        .ticker-wrap{overflow:hidden;white-space:nowrap;}
        .ticker-inner{display:inline-flex;animation:ticker 28s linear infinite;}
        .floating{animation:float 4s ease-in-out infinite;}
      `}</style>

      <Nav />

      <div style={{
        minHeight:'100vh',
        background:'radial-gradient(ellipse at 20% 0%,#120a2e 0%,#0a0a12 55%,#0e0e1a 100%)',
        paddingTop:72, position:'relative', overflow:'hidden'
      }}>
        {/* Orbs */}
        <div style={{position:'absolute',top:-120,left:'10%',width:400,height:400,borderRadius:'50%',background:'radial-gradient(circle,rgba(124,58,237,0.18) 0%,transparent 70%)',pointerEvents:'none'}} />
        <div style={{position:'absolute',top:200,right:'-5%',width:300,height:300,borderRadius:'50%',background:'radial-gradient(circle,rgba(3,105,161,0.15) 0%,transparent 70%)',pointerEvents:'none'}} />

        {/* Ticker */}
        <div style={{background:'rgba(124,58,237,0.12)',borderBottom:'1px solid rgba(124,58,237,0.2)',padding:'8px 0'}}>
          <div className="ticker-wrap">
            <div className="ticker-inner">
              {[...['Script Analysis','Film Budgeting','Casting AI','Location Scouting','Indian Cinema','Pre-Production Tools','ProducerBuddy','AI Filmmaking'],
                ...['Script Analysis','Film Budgeting','Casting AI','Location Scouting','Indian Cinema','Pre-Production Tools','ProducerBuddy','AI Filmmaking']]
                .map((t,i) => (
                  <span key={i} style={{fontSize:11,color:'rgba(167,139,250,0.7)',letterSpacing:'0.12em',textTransform:'uppercase',padding:'0 28px'}}>
                    {t} <span style={{opacity:0.3,marginLeft:28}}>◆</span>
                  </span>
                ))}
            </div>
          </div>
        </div>

        {/* ── MAIN CONTENT ── */}
        {visible && !done && (
          <div style={{maxWidth:960,margin:'0 auto',padding:'0 24px 100px'}}>

            <div className="anim-0" style={{textAlign:'center',padding:'64px 0 48px'}}>
              <div style={{
                display:'inline-flex',alignItems:'center',gap:8,
                background:'rgba(167,139,250,0.1)',border:'1px solid rgba(167,139,250,0.25)',
                borderRadius:99,padding:'6px 18px',marginBottom:28,
                fontSize:11,color:'rgba(167,139,250,0.9)',letterSpacing:'0.1em',textTransform:'uppercase'
              }}>
                <span style={{width:6,height:6,borderRadius:'50%',background:'#a78bfa',display:'inline-block',animation:'pulse-ring 2s infinite'}} />
                Soft Launch — Early Access Open
              </div>

              {displayName && (
                <p style={{fontSize:14,color:'rgba(255,255,255,0.45)',marginBottom:12,fontFamily:"'DM Mono',monospace"}}>
                  Welcome back, <span style={{color:'#a78bfa'}}>{displayName}</span>
                </p>
              )}

              <h1 style={{
                fontFamily:"'Cormorant Garamond',Georgia,serif",
                fontSize:'clamp(36px,6vw,72px)',
                fontWeight:700,color:'#fff',lineHeight:1.05,marginBottom:20,letterSpacing:'-0.01em'
              }}>
                The AI Co-Producer<br />
                <span style={{fontStyle:'italic',background:'linear-gradient(135deg,#a78bfa,#60a5fa)',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent'}}>
                  Indian Cinema
                </span> has been waiting for
              </h1>

              <p style={{fontSize:17,color:'rgba(255,255,255,0.55)',maxWidth:540,margin:'0 auto 40px',lineHeight:1.8,fontWeight:300,fontFamily:"'DM Sans',sans-serif"}}>
                ProducerBuddy brings artificial intelligence to every stage of film production — starting with pre-production tools available right now.
              </p>

              <div className="anim-1" style={{display:'flex',gap:0,justifyContent:'center',borderTop:'1px solid rgba(255,255,255,0.07)',paddingTop:36,marginTop:8}}>
                {[
                  {n:1800,s:'+',label:'Films produced in India yearly'},
                  {n:11833,s:' Cr',label:'Indian box office 2024'},
                  {n:20,s:'%',label:'Lifetime discount for you'},
                ].map((stat,i) => (
                  <div key={i} style={{flex:1,padding:'0 24px',borderRight:i<2?'1px solid rgba(255,255,255,0.07)':'none',textAlign:'center'}}>
                    <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:'clamp(28px,4vw,44px)',fontWeight:700,color:'#fff',lineHeight:1}}>
                      <Counter to={stat.n} suffix={stat.s} />
                    </div>
                    <div style={{fontSize:12,color:'rgba(255,255,255,0.35)',marginTop:6,letterSpacing:'0.04em'}}>{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Benefits */}
            <div className="anim-2" style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:56}}>
              {[
                {icon:'◈',title:'Exclusive Early Benefits',sub:'Free access during soft launch while we perfect the product with you'},
                {icon:'◇',title:'20% Discount on First Purchase',sub:'Locked in forever — your reward for believing early'},
                {icon:'◉',title:'Priority Access',sub:'Your feedback shapes the product roadmap directly'},
              ].map((b,i) => (
                <div key={i} style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.08)',borderRadius:14,padding:'20px 18px',textAlign:'center'}}>
                  <div style={{fontSize:22,marginBottom:8,color:'#a78bfa'}}>{b.icon}</div>
                  <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:17,fontWeight:600,color:'#fff',marginBottom:5}}>{b.title}</div>
                  <div style={{fontSize:12,color:'rgba(255,255,255,0.4)',lineHeight:1.6}}>{b.sub}</div>
                </div>
              ))}
            </div>

            {/* Tool selection */}
            <div className="anim-3">
              <div style={{textAlign:'center',marginBottom:28}}>
                <p style={{fontSize:12,letterSpacing:'0.12em',color:'rgba(167,139,250,0.6)',textTransform:'uppercase',marginBottom:10}}>Choose your first tool</p>
                <h2 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:'clamp(24px,3.5vw,38px)',fontWeight:700,color:'#fff',lineHeight:1.15}}>
                  Which tool excites you most?
                </h2>
              </div>

              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:36}}>
                {TOOLS.map(t => {
                  const isSelected = selectedTool === t.id;
                  const isHovered  = hoveredTool === t.id;
                  const rgb = t.accent === '#7c3aed' ? '124,58,237' : t.accent === '#0369a1' ? '3,105,161' : t.accent === '#b45309' ? '180,83,9' : '4,120,87';
                  return (
                    <div key={t.id} className="tool-card"
                      onClick={() => setSelectedTool(t.id)}
                      onMouseEnter={() => setHoveredTool(t.id)}
                      onMouseLeave={() => setHoveredTool(null)}
                      style={{
                        background: isSelected ? `rgba(${rgb},0.15)` : 'rgba(255,255,255,0.03)',
                        border: isSelected ? `1.5px solid ${t.accent}` : isHovered ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(255,255,255,0.07)',
                        borderRadius:16,padding:'22px 20px',position:'relative',overflow:'hidden',
                        boxShadow: isSelected ? `0 0 32px rgba(${rgb},0.2)` : 'none'
                      }}>
                      {isSelected && (
                        <div style={{position:'absolute',top:12,right:12,width:20,height:20,borderRadius:'50%',background:t.accent,display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,color:'#fff',fontWeight:700}}>✓</div>
                      )}
                      <div style={{fontSize:28,marginBottom:12,color:isSelected?t.accent:'rgba(255,255,255,0.5)',transition:'color 0.2s',fontFamily:"'DM Mono',monospace"}}>{t.icon}</div>
                      <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:20,fontWeight:600,color:'#fff',marginBottom:4}}>{t.name}</div>
                      <div style={{fontSize:12,color:isSelected?t.accent:'rgba(255,255,255,0.4)',marginBottom:10,fontStyle:'italic',transition:'color 0.2s'}}>{t.tagline}</div>
                      <div style={{fontSize:12,color:'rgba(255,255,255,0.45)',lineHeight:1.7,maxHeight:isSelected||isHovered?'80px':'0',overflow:'hidden',transition:'max-height 0.35s cubic-bezier(.22,1,.36,1),opacity 0.3s',opacity:isSelected||isHovered?1:0}}>
                        {t.detail}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="anim-4" style={{textAlign:'center'}}>
                {selectedTool && (
                  <p style={{fontSize:13,color:'rgba(255,255,255,0.45)',marginBottom:16,fontStyle:'italic',animation:'fadeIn 0.3s ease'}}>
                    Great choice — <span style={{color:tool?.accent}}>{tool?.name}</span> will be unlocked first for you
                  </p>
                )}

                {error && (
                  <div style={{background:'rgba(220,38,38,0.15)',border:'1px solid rgba(220,38,38,0.3)',borderRadius:8,padding:'10px 16px',fontSize:13,color:'#fca5a5',marginBottom:16}}>
                    {error}
                  </div>
                )}

                <button onClick={handleReserve} disabled={!selectedTool||submitting}
                  className="reserve-btn"
                  style={{
                    padding:'16px 48px',
                    background:selectedTool?`linear-gradient(135deg,${tool?.accent},#0369a1)`:'rgba(255,255,255,0.08)',
                    color:selectedTool?'#fff':'rgba(255,255,255,0.3)',
                    border:selectedTool?'none':'1px solid rgba(255,255,255,0.1)',
                    borderRadius:12,fontSize:16,fontWeight:500,
                    cursor:selectedTool&&!submitting?'pointer':'not-allowed',
                    fontFamily:"'DM Sans',sans-serif",letterSpacing:'0.01em',
                    animation:selectedTool?'pulse-ring 2.5s infinite':'none'
                  }}>
                  {submitting?'Reserving your spot...':selectedTool?'Reserve Free Access →':'Select a tool to continue'}
                </button>

                <div style={{display:'flex',gap:20,justifyContent:'center',marginTop:16,flexWrap:'wrap'}}>
                  {['Time Saving','Cost Reduction','Your AI Production Buddy'].map(t => (
                    <span key={t} style={{fontSize:12,color:'rgba(255,255,255,0.25)',display:'flex',alignItems:'center',gap:5}}>
                      <span style={{color:'rgba(167,139,250,0.5)'}}>◆</span> {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── SUCCESS STATE ── */}
        {done && (
          <div style={{maxWidth:600,margin:'0 auto',padding:'80px 24px',textAlign:'center',animation:'fadeSlideUp 0.65s cubic-bezier(.22,1,.36,1) both'}}>
            <div className="floating" style={{width:80,height:80,borderRadius:'50%',background:'linear-gradient(135deg,#7c3aed,#0369a1)',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 28px',fontSize:32}}>
              ✦
            </div>
            <h2 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:'clamp(28px,5vw,52px)',fontWeight:700,color:'#fff',marginBottom:16}}>
              {alreadyRegistered ? 'You\'re already in!' : 'You\'re in.'}
            </h2>
            {displayName && (
              <p style={{fontSize:14,color:'rgba(167,139,250,0.7)',marginBottom:8,fontFamily:"'DM Mono',monospace"}}>
                {displayName}
              </p>
            )}
            <p style={{fontSize:16,color:'rgba(255,255,255,0.5)',lineHeight:1.8,marginBottom:8}}>
              {alreadyRegistered
                ? `Your early access to ${selectedTool} is already reserved.`
                : `Your early access to `}
              {!alreadyRegistered && <span style={{color:tool?.accent||'#a78bfa'}}>{tool?.name}</span>}
              {!alreadyRegistered && ' has been reserved. We\'ll reach out personally as soon as it\'s ready for you.'}
            </p>
            <p style={{fontSize:13,color:'rgba(255,255,255,0.3)',marginBottom:36}}>
              Your 20% lifetime discount is locked in.
            </p>
            <button onClick={goToDashboard}
              style={{
                padding:'14px 36px',
                background:'rgba(255,255,255,0.08)',
                border:'1px solid rgba(255,255,255,0.15)',
                borderRadius:10,fontSize:14,color:'rgba(255,255,255,0.7)',
                cursor:'pointer',fontFamily:"'DM Sans',sans-serif"
              }}>
              Go to My Dashboard →
            </button>
          </div>
        )}
      </div>
    </>
  );
}
